/**
 * The dream API (Dreams UX pass 2026-09-06). Every read returns the same flat
 * shape the backend's `dream_row` writes; the list asks the board once for
 * every dream's movement, which derives from the goals under it — nothing is
 * scheduled or logged on a dream itself.
 */
export type Dream = {
  id: number
  title: string
  description?: string | null
  /** The lifecycle lane: active · commitment · in_progress · parked · paused_by_user · hold · completed · cancelled · chat_suggested. */
  status: string
  progress: number
  priority: 'low' | 'medium' | 'high' | 'critical'
  target_date?: string | null
  category: string
  life_area?: string | null
  life_areas?: string[] | null
  is_active?: boolean
  is_orphan?: boolean
  /** Life-map lane the backend tagged this item with (only on ?include=all). */
  lane?: string
  completed_at?: string | null
  paused_at?: string | null
  let_go_at?: string | null
  created_at?: string
  updated_at?: string | null
}

/** Read options for the list endpoint. `include: 'all'` returns the full
 *  life-map view (every lifecycle state, deduped + lane-tagged). */
export type ListOptions = { include?: 'all' }

export type CreateDreamRequest = {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
  target_date?: string
  category?: string
  life_area?: string
  life_areas?: string[]
  status?: string
  progress?: number
}

export type UpdateDreamRequest = {
  title?: string
  description?: string | null
  status?: string
  progress?: number
  priority?: 'low' | 'medium' | 'high' | 'critical'
  target_date?: string | null
  category?: string
  life_area?: string | null
  life_areas?: string[]
  is_active?: boolean
}

/** A goal under a dream, with its own board card. */
export interface DreamGoalItem {
  id: number
  title: string
  status?: string | null
  progress: number
  priority?: string | null
  life_area?: string | null
  target_date?: string | null
  journey_step_id?: string | null
  dream_journey_plan_id?: number | null
  created_at?: string | null
  completed_at?: string | null
  live: boolean
  card: {
    path?: { status: string; done: number; total: number } | null
    next?: { kind: 'task' | 'habit' | 'skill'; id: number; title: string | null; date: string | null; time: string | null; at: string | null } | null
    today?: { kind: string; id: number; title: string | null; at: string | null } | null
    last_14?: Array<{ date: string; outcome: 'done' | null }>
    days_active?: number
    window_days?: number
    window_capped?: boolean
    days_total?: number
    last_at?: string | null
    last_date?: string | null
    open_tasks?: number
    done_tasks?: number
    review?: unknown | null
    words?: string
  } | null
}

/** A dream's card on the list page (GET /api/hierarchy/dreams/board). */
export interface DreamBoardCard {
  path: {
    plan_id: number; status: string; done: number; total: number
    current: { step_id: string; kind: 'goal'; title: string | null; id: number | null } | null
    entity_ids: number[]
  } | null
  goals: DreamGoalItem[]
  /** The next thing to do: the current journey goal's own next, the soonest next among live goals, or a goal that still needs a next step. */
  next: { kind: 'task' | 'habit' | 'skill' | 'goal'; id: number; title: string | null; date: string | null; time: string | null; at: string | null; via: { kind: 'goal'; id: number; title: string | null } | null } | null
  today: { kind: string; id: number; title: string | null; at: string | null; goal_id: number; goal_title: string | null } | null
  last_14: Array<{ date: string; outcome: 'done' | null }>
  days_active: number
  window_days: number
  window_capped: boolean
  days_total: number
  last_at: string | null
  last_date: string | null
  progress: number | null
  derived_progress: number | null
  open_goals: number
  done_goals: number
  words: string
}

export interface DreamBoard { today: string; dreams: Record<string, DreamBoardCard> }

export interface DreamJourneyStep {
  step_id: string
  order: number
  title: string
  description: string
  estimated_duration_minutes: number
  child_type?: 'goal' | string
  status: 'locked' | 'unlocked' | 'completed'
  entity_id?: number | null
  entity_type?: string | null
  unlocked_at?: string | null
  completed_at?: string | null
}

export interface DreamJourneyPlan {
  journey_plan_id: number
  dream_id: number
  status: 'active' | 'completed' | 'archived'
  current_step_index: number
  active_entity_id?: number | null
  active_entity_type?: string | null
  steps: DreamJourneyStep[]
  created_at?: string
  updated_at?: string
}

/** A step as sent to accept-breakdown / the journey edit: always a goal. */
export interface DreamBreakdownStep {
  step_id?: string
  title: string
  description: string
  estimated_duration_minutes: number
  order: number
  child_type?: 'goal'
}

async function readJson(r: Response, fallback: string) {
  const body = await r.json().catch(() => ({}))
  if (!r.ok || body.success === false) throw new Error(body.error || fallback)
  return body
}

export class DreamService {
  private baseUrl = '/api/hierarchy/dreams'

  async getDreams(opts?: ListOptions): Promise<Dream[]> {
    const response = await fetch(this.baseUrl + (opts?.include === 'all' ? '?include=all' : ''), {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'manual',
    })
    if (response.status === 302 || response.status === 301 || response.type === 'opaqueredirect' || response.status === 401 || response.status === 403) {
      throw new Error('Authentication required. Please log in to access dreams.')
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch dreams: ${response.status}` }))
      throw new Error(error.error || `Failed to fetch dreams: ${response.status}`)
    }
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  async getDream(id: number | string): Promise<Dream> {
    // The generic item endpoint returns { data: { item, parent_chain, children } } (or { item }); the item is the flat row.
    const response = await fetch(`/api/hierarchy/item/dream/${id}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: `Failed to fetch dream ${id}` }))
      throw new Error(error.error || `Failed to fetch dream ${id}: ${response.status}`)
    }
    const result = await response.json()
    const item = (result.data?.item || result.item || result.data || result) as Dream
    if (!item || item.title == null) throw new Error(`Dream ${id} not found`)
    return item
  }

  async getBoard(today: string): Promise<DreamBoard> {
    const r = await fetch(`${this.baseUrl}/board?today=${encodeURIComponent(today)}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    const body = await readJson(r, 'Could not load the dreams board')
    return body.data as DreamBoard
  }

  async createDream(dream: CreateDreamRequest): Promise<Dream> {
    const response = await fetch(this.baseUrl, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dream),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create dream' }))
      throw new Error(error.error || 'Failed to create dream')
    }
    const data = await response.json()
    return (data.data && data.data.id ? data.data : data) as Dream
  }

  async updateDream(id: number | string, updates: UpdateDreamRequest): Promise<Dream> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update dream' }))
      throw new Error(error.error || 'Failed to update dream')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteDream(id: number | string): Promise<{ cleanup: Record<string, number> }> {
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'DELETE', credentials: 'include' })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(body.error || 'Failed to delete dream')
    return { cleanup: (body && body.cleanup) || {} }
  }

  async updateDreamProgress(id: number | string, progress: number): Promise<Dream> {
    return this.updateDream(id, { progress })
  }

  async markDreamComplete(id: number | string): Promise<Dream> {
    return this.updateDream(id, { status: 'completed', progress: 100 })
  }

  async getOccurrences(id: number | string): Promise<unknown> {
    const r = await fetch(`${this.baseUrl}/${id}/occurrences`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    return (await readJson(r, 'Could not load what got done')).data
  }

  async previewBreakdown(dreamId: number | string): Promise<{ steps: DreamBreakdownStep[] }> {
    const response = await fetch(`${this.baseUrl}/${dreamId}/preview-breakdown`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not plan the journey' }))
      throw new Error(err.error || 'Could not plan the journey')
    }
    return response.json()
  }

  async acceptBreakdown(dreamId: number | string, steps: DreamBreakdownStep[]): Promise<{ journey_plan_id: number | null; created_entity: { id: number; title: string } | null; steps: Array<{ step_id: string; title?: string; status: string; child_type?: string; entity_id?: number | null }> }> {
    const response = await fetch(`${this.baseUrl}/${dreamId}/accept-breakdown`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not start the journey' }))
      throw new Error(err.error || 'Could not start the journey')
    }
    return response.json()
  }

  async getJourneyPlan(dreamId: number | string): Promise<DreamJourneyPlan | null> {
    const response = await fetch(`${this.baseUrl}/${dreamId}/journey-plan`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch journey plan')
    return response.json()
  }

  async updateJourneyPlan(dreamId: number | string, steps: DreamBreakdownStep[]): Promise<DreamJourneyPlan> {
    const response = await fetch(`${this.baseUrl}/${dreamId}/journey-plan`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not save the journey' }))
      throw new Error(err.error || 'Could not save the journey')
    }
    return response.json()
  }
}
